import { useEffect } from 'react'
import { GetServerSideProps } from 'next'
import styled from 'styled-components'
import { BackActionContainer } from '@/components/common/back-action/back-action-container'
import { ListProductFilter } from '@/components/common/list'
import { SkeletonLine } from '@/components/domains/category/SkeletonLine'
import { LayoutHeader } from '@/components/domains/layouts'
import SeoHeader from '@/components/domains/layouts/header/SeoHeader'
import ListCategoriesSliderV2 from '@/components/domains/list/ListCategoriesSliderV2'
import { ListProductsV2 } from '@/components/domains/list/ListProductsV2'
import { Z_INDEXES } from '@/constants/legacy/constLayout'
import { FontStyles } from '@/constants/legacy/constTypeface'
import { TrackerOrderByEnum } from '@/constants/tracker-sort-by-value.enum'
import { useCategoryTracker } from '@/containers/event-tracker/category.event-tracker'
import { useCustomRouter } from '@/containers/hooks'
import useCategoriesProductListV2 from '@/containers/hooks/categories/productsList/rootCategories/productsListV2.hook'
import useCategoryContainerHook from '@/containers/hooks/category.container.hook'
import usePetType from '@/containers/hooks/usePetType'
import { RootCategoriesDocument } from '@/graphql/generated/hooks'
import { AdPetType } from '@/graphql/generated/schemas'
import { FitpetMallEventEnum } from '@/utils/event-tracker/events'
import { decodeId } from '@/utils/utilApi'
import { addApolloState, initializeApollo } from '@/utils/utilApolloClient'
import { getPetTypeRootCategoryKoreanText } from '@/utils/utilCategory'

const SubCategorySlugPageV2 = () => {
  const { trackViewCategoryEvent } = useCategoryTracker()
  const { query } = useCustomRouter()
  const { setPetType, getPetType } = usePetType()

  const {
    rootCategorySlug,
    subCategorySlug,
    petType,
    orderBy: orderByQuery,
  } = query as {
    rootCategorySlug: string
    subCategorySlug: string
    trackingCategoryId?: string
    petType?: AdPetType
    orderBy?: string
  }

  const selectedPetType = petType || getPetType()
  setPetType(selectedPetType)

  const { rootCategory, subCategory } = useCategoryContainerHook({
    fetchPolicy: 'cache-first',
    petType: selectedPetType,
    rootCategorySlug,
    subCategorySlug,
  })

  const isAll = subCategorySlug === 'all'
  const categoryId = (isAll ? rootCategory?.id : subCategory?.id) || ''

  const { orderBy, listTotalCount, isLoading } = useCategoriesProductListV2({
    categoryId,
  })

  const petTypeKoreaText = getPetTypeRootCategoryKoreanText(selectedPetType)
  const rootCategoryName = rootCategory?.name || ''
  const subCategoryName = isAll ? '전체' : subCategory?.name
  const metaSubCategoryName = (subCategoryName || '').replace(rootCategory?.name || '', '')

  const SEO_META_DATA = {
    ALL: {
      title: `${petTypeKoreaText} ${rootCategoryName} - 핏펫몰`,
      metaDescription: `핏펫 ${petTypeKoreaText}-${rootCategoryName} 제품 목록입니다. 우리 ${petTypeKoreaText}를 위한 ${rootCategoryName}들을 한자리에 모았습니다. 지금 바로 확인해보세요`,
      pathname: `/category/${rootCategorySlug}/${subCategorySlug}?petType=${selectedPetType}`,
    },
    SUB: {
      title: `${petTypeKoreaText} ${rootCategoryName} | ${metaSubCategoryName} - 핏펫몰`,
      metaDescription: `핏펫 ${petTypeKoreaText}-${metaSubCategoryName}-${rootCategoryName} 제품 목록입니다. 우리 ${petTypeKoreaText}를 위한 ${metaSubCategoryName}-${rootCategoryName}들을 한자리에 모았습니다. 지금 바로 확인해보세요`,
      pathname: `/category/${rootCategorySlug}/${subCategorySlug}?petType=${selectedPetType}`,
    },
  }

  useEffect(() => {
    if (!rootCategoryName || !subCategoryName) {
      return
    }

    const sortBy = (() => {
      const mapped = {
        CREATED: TrackerOrderByEnum.Recent,
        SALES_SCORE: TrackerOrderByEnum.Popular,
        MD_RECOMMENDED: TrackerOrderByEnum.Recommend,
        DISCOUNT_RATE: TrackerOrderByEnum.Discount,
        REVIEW_COUNT: TrackerOrderByEnum.Review,
        REVIEW_SCORE: TrackerOrderByEnum.ReviewScore,
        SALE_PRICE_DESC: TrackerOrderByEnum.HighPrice,
        SALE_PRICE_ASC: TrackerOrderByEnum.LowPrice,
      }
      // @ts-ignore
      return mapped[orderBy]
    })()
    trackViewCategoryEvent({
      categoryTypeName: rootCategoryName,
      categoryName: (subCategoryName || '').replace(/~/g, ','),
      sortBy,
      isDirectResult: !orderByQuery,
    })
  }, [rootCategory?.name, subCategory?.name, orderByQuery, orderBy])

  return (
    <>
      {isAll ? (
        <SeoHeader
          title={SEO_META_DATA.ALL.title}
          metaDescription={SEO_META_DATA.ALL.metaDescription}
          pathname={SEO_META_DATA.ALL.pathname}
        />
      ) : (
        <SeoHeader
          title={SEO_META_DATA.SUB.title}
          metaDescription={SEO_META_DATA.SUB.metaDescription}
          pathname={SEO_META_DATA.SUB.pathname}
        />
      )}
      <LayoutHeader />
      <SliderContainer>
        <ListCategoriesSliderV2
          isAll={isAll}
          rootCategory={rootCategory}
          subCategory={subCategory}
          petType={selectedPetType}
        />
      </SliderContainer>
      <StyledBreadCrumbsContainer>
        {isLoading ? (
          <SkeletonLine height="2rem" width="8rem" />
        ) : (
          <StyledBreadCrumbs>{`${petTypeKoreaText} > ${rootCategoryName} > ${subCategoryName}`}</StyledBreadCrumbs>
        )}
      </StyledBreadCrumbsContainer>
      <BackActionContainer name="recommend-list-for-category-list">
        <ListContainer>
          <ListProductFilter isLoading={isLoading} orderBy={orderBy} totalCount={listTotalCount} />
          <ListProductsV2
            currentCategoryId={categoryId}
            trackingInfo={{
              eventName: FitpetMallEventEnum.ClickCategoryListProduct,
              categoryTypeName: rootCategoryName,
              categoryId: Number(decodeId((isAll ? rootCategory?.id : subCategory?.id) || '').id),
              categoryName: isAll ? '전체' : subCategoryName || '',
            }}
          />
        </ListContainer>
      </BackActionContainer>
    </>
  )
}

const SliderContainer = styled.div`
  position: absolute;
  z-index: ${Z_INDEXES.HOME_CATEGORY_SLIDER};
`

const StyledBreadCrumbsContainer = styled.div`
  padding: 11rem 2rem 0;
`

const StyledBreadCrumbs = styled.h1`
  ${FontStyles.body5}
  color: ${(props) => props.theme.color.gray200};
`

const ListContainer = styled.div`
  padding: 2rem 0;
`

export default SubCategorySlugPageV2

export const getServerSideProps: GetServerSideProps = async () => {
  const apolloClient = initializeApollo()

  await apolloClient.query({
    query: RootCategoriesDocument,
    variables: {
      filter: {
        isRootCategory: true,
        isShown: true,
      },
    },
  })

  return addApolloState(apolloClient, {
    props: {},
  })
}
